// Copyright (c) 2023 embyt GmbH. All rights reserved.
// Author: Marc Planeck

import * as React from "react";
import { connect } from "react-redux";
import { defineMessages, IntlContext, IntlShape } from "react-intl";
import { Button } from "react-bootstrap";

import { DashboardPanel } from "types";
import { IAction, ActionType, raiseAction, IActionTypeSelectGuiItem } from "actions";
import { ITariffCircuit } from "shared/types/parameter";
import { IState } from "states/states";
import { PowerViewMode } from "states/cachedstates";
import { getSiteSelection } from "guiutils";

import TariffCircuitListModel from "model/tariffcircuitlist";

import TrendMemberSelect from "view/comp/TrendMemberSelect";

import SvgHierarchyChart from "icons/iconHierarcyChart";
import SvgSankeyChart from "icons/iconSankeyChart";
import SvgPieChart from "icons/iconPieChart";
import SvgListView from "icons/iconListView";

interface IPowerHeadingExtProps {
  viewModes: PowerViewMode[];
  panel?: DashboardPanel;
}

interface IPowerHeadingProps {
  powerViewMode: PowerViewMode;
  tariffCircuits: ITariffCircuit[];
}

const mapStateToProps = (state: IState, ownProps: IPowerHeadingExtProps) => {
  const selection = getSiteSelection(state);
  const props: IPowerHeadingProps = {
    powerViewMode: selection?.powerViewMode ?? PowerViewMode.TABLE,
    tariffCircuits: state.para.parameter?.tariffline ?? [],
  };
  return props;
};

interface IPowerHeadingDispatch {
  onSwitchPowerView: (selection: PowerViewMode) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IPowerHeadingDispatch = {
    onSwitchPowerView: (selection: PowerViewMode) => {
      const payload: IActionTypeSelectGuiItem = { selection };
      dispatch(raiseAction(ActionType.ACT_SELECT_POWERVIEWMODE, payload));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  switchToHierarchy: {
    id: "switch to hierarchy view",
  },
  switchtoSankey: {
    id: "switch to sankey view",
  },
  switchToPie: {
    id: "switch to pie-chart view",
  },
  switchToList: {
    id: "switch to list view",
  },
});

class PowerHeadingComponent extends React.Component<
  IPowerHeadingProps & IPowerHeadingExtProps & IPowerHeadingDispatch
> {
  static contextType = IntlContext;
  declare context: IntlShape;

  public render() {
    return (
      <div className="headerbuttons mb-2">
        <div className="left"></div>
        {this.renderViewModes()}
      </div>
    );
  }

  private renderViewModes() {
    const hasHierarchies = TariffCircuitListModel.hasHierarchy(this.props.tariffCircuits);
    const showSankeyAndHierarchy = hasHierarchies;

    return (
      this.props.viewModes.length > 1 && (
        <div id="graphswitch">
          {(this.props.panel ?? DashboardPanel.POWER) !== DashboardPanel.POWER && (
            <TrendMemberSelect
              className="ms-2"
              showOnlyPower={(this.props.panel ?? DashboardPanel.POWER) === DashboardPanel.POWER}
            />
          )}
          {this.props.viewModes.includes(PowerViewMode.HIERARCY) === true &&
            showSankeyAndHierarchy && (
              <Button
                variant="secondary"
                className={
                  this.props.powerViewMode === PowerViewMode.HIERARCY ? "active" : undefined
                }
                title={this.context.formatMessage(messages.switchToHierarchy)}
                onClick={this.props.onSwitchPowerView.bind(this, PowerViewMode.HIERARCY)}
              >
                <SvgHierarchyChart />
              </Button>
            )}
          {this.props.viewModes.includes(PowerViewMode.SANKEY) === true &&
            showSankeyAndHierarchy && (
              <Button
                variant="secondary"
                className={this.props.powerViewMode === PowerViewMode.SANKEY ? "active" : undefined}
                title={this.context.formatMessage(messages.switchtoSankey)}
                onClick={this.props.onSwitchPowerView.bind(this, PowerViewMode.SANKEY)}
              >
                <SvgSankeyChart />
              </Button>
            )}
          {this.props.viewModes.includes(PowerViewMode.PIE) === true && (
            <Button
              variant="secondary"
              className={this.props.powerViewMode === PowerViewMode.PIE ? "active" : undefined}
              title={this.context.formatMessage(messages.switchToPie)}
              onClick={this.props.onSwitchPowerView.bind(this, PowerViewMode.PIE)}
            >
              <SvgPieChart />
            </Button>
          )}
          {this.props.viewModes.includes(PowerViewMode.TABLE) === true && (
            <Button
              variant="secondary"
              className={this.props.powerViewMode === PowerViewMode.TABLE ? "active" : undefined}
              title={this.context.formatMessage(messages.switchToList)}
              onClick={this.props.onSwitchPowerView.bind(this, PowerViewMode.TABLE)}
            >
              <SvgListView />
            </Button>
          )}
        </div>
      )
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(PowerHeadingComponent);
